import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridRowModel,
} from '@mui/x-data-grid-premium'
import {
  useOptimiserInputTable,
  useOptimiserRunOutput,
  useOptimiserView,
  useUpdateOptimiserInputTable,
  useUpdateOptimiserView,
} from './Optimiser.data'
import { TableSection } from './Optimiser.sections'
import { TOKENS, FONT_SANS, FONT_MONO, GRID_SX } from './OptimiserDesign'

const isNumericColumn = (
  field: string,
  rows: Record<string, unknown>[]
): boolean => {
  const sample = rows.find((r) => r[field] !== null && r[field] !== undefined)
  return typeof sample?.[field] === 'number'
}

const buildColumns = (
  columns: string[],
  rows: Record<string, unknown>[],
  editable: boolean
): GridColDef[] =>
  columns.map((field) => {
    const numeric = isNumericColumn(field, rows)
    return {
      field,
      headerName: field.replace(/_/g, ' '),
      flex: 1,
      minWidth: 90,
      maxWidth: 300,
      cellClassName: numeric ? 'sch-num' : '',
      editable,
      type: editable && numeric ? 'number' : 'string',
    }
  })

const DEFAULT_PAGE_SIZE = 500

export const OptimiserTable = ({
  section,
  scenarioId,
}: {
  section: TableSection
  scenarioId?: string
}) => {
  const isView = section.type === 'view'
  const isOutput = section.type === 'run-output'
  const isInput = !isView && !isOutput
  const isEditable = isInput || isView // run-output is always read-only
  const isServerPaginated = isView || isOutput

  const effectiveScenarioId = isOutput
    ? (section.scenarioId ?? scenarioId)
    : scenarioId

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  // dirty rows keyed by synthetic row id
  const [editedRows, setEditedRows] = useState<Map<number, GridRowModel>>(
    new Map()
  )

  // Reset pagination and edits when section or scenario changes
  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE })
    setEditedRows(new Map())
  }, [section.id, section.runId, scenarioId])

  const inputQuery = useOptimiserInputTable(
    !isServerPaginated ? section.id : '',
    effectiveScenarioId
  )
  const viewQuery = useOptimiserView(
    isView ? section.id : '',
    paginationModel.page,
    paginationModel.pageSize,
    effectiveScenarioId
  )
  const outputQuery = useOptimiserRunOutput(
    isOutput ? (section.runId ?? '') : '',
    isOutput ? section.id : '',
    paginationModel.page,
    paginationModel.pageSize,
    effectiveScenarioId
  )

  const inputMutation = useUpdateOptimiserInputTable(
    isInput ? section.id : '',
    effectiveScenarioId
  )
  const viewMutation = useUpdateOptimiserView(
    isView ? section.id : '',
    effectiveScenarioId
  )
  const updateMutation = isView ? viewMutation : inputMutation

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = isOutput ? outputQuery : isView ? viewQuery : inputQuery

  const tableName = section.id
  const rawRows = response?.data ?? []
  const columns = response?.columns ?? []

  const rows = useMemo(
    () => rawRows.map((row, i) => ({ id: i, ...row })),
    [rawRows]
  )

  const colDefs = useMemo(
    () => buildColumns(columns, rawRows, isEditable),
    [columns, rawRows, isEditable]
  )

  const processRowUpdate = useCallback((newRow: GridRowModel) => {
    setEditedRows((prev) => new Map(prev).set(newRow.id as number, newRow))
    return newRow
  }, [])

  const handleSave = () => {
    const payload = rows.map((row) => {
      const edited = editedRows.get(row.id as number)
      const merged = edited ? { ...row, ...edited } : row
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...rest } = merged
      return rest as Record<string, unknown>
    })
    updateMutation.mutate(payload, {
      onSuccess: () => setEditedRows(new Map()),
    })
  }

  const handleDiscard = () => setEditedRows(new Map())

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
        height="100%"
      >
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
        gap={2}
      >
        <Typography
          sx={{
            fontFamily: FONT_SANS,
            fontSize: 13,
            color: TOKENS.ink900,
            fontWeight: 600,
          }}
        >
          Failed to load {tableName.replace(/_/g, ' ')}
        </Typography>
        <Typography
          sx={{ fontFamily: FONT_MONO, fontSize: 11, color: TOKENS.ink400 }}
        >
          {(error as Error)?.message ?? 'Unknown error'}
        </Typography>
      </Box>
    )
  }

  const isDirty = editedRows.size > 0

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...GRID_SX,
      }}
    >
      {/* Table header */}
      <Box
        sx={{
          px: 6,
          pt: 4,
          pb: 3,
          borderBottom: `1px solid ${TOKENS.line200}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: 13,
              fontWeight: 700,
              color: TOKENS.ink900,
              textTransform: 'capitalize',
            }}
          >
            {tableName.replace(/_/g, ' ')}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 11,
              color: TOKENS.ink400,
              mt: 0.5,
            }}
          >
            {isServerPaginated ? (response?.total ?? 0) : rows.length} rows
            &middot; {columns.length} columns
            {isOutput && section.runId && (
              <Box component="span" sx={{ ml: 3, color: TOKENS.ink300 }}>
                {section.runId}
              </Box>
            )}
          </Typography>
        </Box>

        {/* Save / Discard — only shown when edits are pending */}
        {isEditable && isDirty && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                fontSize: 11,
                fontFamily: FONT_SANS,
                color: TOKENS.ink400,
              }}
            >
              {editedRows.size} unsaved{' '}
              {editedRows.size === 1 ? 'change' : 'changes'}
            </Box>
            <Box
              onClick={handleDiscard}
              sx={{
                px: '10px',
                py: '5px',
                borderRadius: '4px',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT_SANS,
                cursor: 'pointer',
                border: `1px solid ${TOKENS.line300}`,
                color: TOKENS.ink700,
                '&:hover': { borderColor: TOKENS.ink400 },
              }}
            >
              Discard
            </Box>
            <Box
              onClick={updateMutation.isPending ? undefined : handleSave}
              sx={{
                px: '10px',
                py: '5px',
                borderRadius: '4px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: FONT_SANS,
                cursor: updateMutation.isPending ? 'default' : 'pointer',
                background: updateMutation.isPending
                  ? TOKENS.line200
                  : TOKENS.brandTeal,
                color: updateMutation.isPending
                  ? TOKENS.ink300
                  : TOKENS.surface0,
              }}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridPremium
          rows={rows}
          columns={colDefs}
          density="compact"
          disableRowSelectionOnClick
          editMode="cell"
          processRowUpdate={isEditable ? processRowUpdate : undefined}
          pageSizeOptions={[100, 500, 1000]}
          paginationModel={isServerPaginated ? paginationModel : undefined}
          onPaginationModelChange={
            isServerPaginated ? setPaginationModel : undefined
          }
          paginationMode={isServerPaginated ? 'server' : 'client'}
          rowCount={isServerPaginated ? (response?.total ?? 0) : undefined}
          initialState={
            isServerPaginated
              ? undefined
              : { pagination: { paginationModel: { pageSize: 100 } } }
          }
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? 'row--even'
              : 'row--odd'
          }
          sx={{ border: 'none', height: '100%' }}
        />
      </Box>
    </Box>
  )
}
